import { API_ORIGIN } from "../api";
import type { CloudConfig, Manual, RemoteRef } from "../types";
import type { CloudAdapter } from "./types";

declare global {
  interface Window {
    google?: {
      accounts?: {
        oauth2?: {
          initTokenClient(options: {
            client_id: string;
            scope: string;
            callback: (response: { access_token?: string; error?: string }) => void;
          }): { requestAccessToken(options?: { prompt?: string }): void };
        };
      };
    };
  }
}

const GIS_SRC = "https://accounts.google.com/gsi/client";
const DRIVE_SCOPE = "https://www.googleapis.com/auth/drive.file";
let accessToken = sessionStorage.getItem("allmanual.google.accessToken") || "";
const grantedScopes = new Set<string>();

function loadScript(src: string, id: string) {
  return new Promise<void>((resolve, reject) => {
    if (document.getElementById(id)) return resolve();
    const script = document.createElement("script");
    script.id = id;
    script.src = src;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.head.appendChild(script);
  });
}

async function driveFetch(path: string, init: RequestInit = {}) {
  if (!accessToken) throw new Error("Google Drive 연결이 필요합니다.");
  const response = await fetch(path.startsWith("http") ? path : `https://www.googleapis.com/drive/v3${path}`, {
    ...init,
    headers: {
      Authorization: `Bearer ${accessToken}`,
      ...(init.headers || {})
    }
  });
  if (!response.ok) throw new Error(await response.text());
  return response;
}

function multipartBody(metadata: object, blob: Blob) {
  const boundary = `allmanual_${crypto.randomUUID()}`;
  const body = new Blob(
    [
      `--${boundary}\r\nContent-Type: application/json; charset=UTF-8\r\n\r\n${JSON.stringify(metadata)}\r\n`,
      `--${boundary}\r\nContent-Type: ${blob.type || "application/octet-stream"}\r\n\r\n`,
      blob,
      `\r\n--${boundary}--`
    ],
    { type: `multipart/related; boundary=${boundary}` }
  );
  return { body, contentType: body.type };
}

async function ensureFolder(name: string) {
  const query = encodeURIComponent(`mimeType='application/vnd.google-apps.folder' and name='${name}' and trashed=false`);
  const found = await driveFetch(`/files?q=${query}&fields=files(id,name)`).then((response) => response.json());
  if (found.files?.[0]?.id) return found.files[0].id as string;
  const created = await driveFetch("/files?fields=id,name,webViewLink", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name, mimeType: "application/vnd.google-apps.folder" })
  }).then((response) => response.json());
  return created.id as string;
}

async function uploadBlob(name: string, blob: Blob, folderId: string) {
  const { body, contentType } = multipartBody({ name, parents: [folderId] }, blob);
  return driveFetch("https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart&fields=id,name,webViewLink", {
    method: "POST",
    headers: { "Content-Type": contentType },
    body
  }).then((response) => response.json());
}

export function createGoogleDriveAdapter(config: CloudConfig["google"]): CloudAdapter {
  return {
    provider: "google",
    mode: "live",
    async connect() {
      if (!config.clientId) throw new Error("GOOGLE_CLIENT_ID가 필요합니다.");
      await loadScript(GIS_SRC, "google-gis");
      await new Promise<void>((resolve, reject) => {
        const client = window.google?.accounts?.oauth2?.initTokenClient({
          client_id: config.clientId,
          scope: DRIVE_SCOPE,
          callback: (response) => {
            if (response.error || !response.access_token) return reject(new Error(response.error || "Google token missing"));
            accessToken = response.access_token;
            sessionStorage.setItem("allmanual.google.accessToken", accessToken);
            DRIVE_SCOPE.split(" ").forEach((scope) => grantedScopes.add(scope));
            resolve();
          }
        });
        client?.requestAccessToken({ prompt: accessToken && grantedScopes.has(DRIVE_SCOPE) ? "" : "consent" });
      });
    },
    async disconnect() {
      accessToken = "";
      sessionStorage.removeItem("allmanual.google.accessToken");
    },
    async uploadManual(manual: Manual): Promise<RemoteRef> {
      if (!accessToken) await this.connect();
      const folderId = await ensureFolder("ALLManual");
      for (const image of manual.originalImages) {
        const blob = await fetch(`${API_ORIGIN}${image}`).then((response) => response.blob());
        await uploadBlob(`${manual.id}-${image.split("/").pop() || "image"}`, blob, folderId);
      }
      const payload = new Blob([JSON.stringify(manual, null, 2)], { type: "application/json" });
      const file = await uploadBlob(`${manual.id}.allmanual.json`, payload, folderId);
      return {
        provider: "google",
        remoteId: file.id,
        url: file.webViewLink,
        folderId,
        syncedAt: new Date().toISOString(),
        mode: "live"
      };
    },
    async listManuals() {
      if (!accessToken) await this.connect();
      const folderId = await ensureFolder("ALLManual");
      const query = encodeURIComponent(`'${folderId}' in parents and name contains '.allmanual.json' and trashed=false`);
      const files = await driveFetch(`/files?q=${query}&fields=files(id,name)`).then((response) => response.json());
      const manuals: Manual[] = [];
      for (const file of files.files || []) {
        const text = await driveFetch(`/files/${file.id}?alt=media`).then((response) => response.text());
        manuals.push(JSON.parse(text));
      }
      return manuals;
    }
  };
}
